import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faPaperPlane, faStar, faCheck } from '@fortawesome/free-solid-svg-icons';

const BASE_URL = 'https://ar-caba-sv224.seconline.net.ar:16001/api';
const ACCENT = '#4E67EB';

async function postJSON(path, body) {
    const res = await fetch(`${BASE_URL}${path}`, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    return { status: res.status, data: await res.json() };
}

function IOSChat({ embedded = false }) {
    const [messages, setMessages]     = useState([]);
    const [input, setInput]           = useState('');
    const [threadId, setThreadId]     = useState(null);
    const [loading, setLoading]       = useState(false);

    // Review state
    const [reviewingId, setReviewingId]         = useState(null);
    const [reviewComment, setReviewComment]     = useState('');
    const [selectedPoints, setSelectedPoints]   = useState(null);
    const [reviewedMap, setReviewedMap]         = useState({});
    const [sendingReview, setSendingReview]     = useState(false);

    const bottomRef = useRef(null);

    useEffect(() => { document.title = 'Fasta - Chat con IA'; }, []);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const send = async () => {
        const text = input.trim();
        if (!text) return;

        setMessages((prev) => [...prev, { role: 'user', text }]);
        setInput('');
        setLoading(true);

        try {
            const body = { text, scope: "UNIT", ...(threadId && { threadId }) };
            const { status, data } = await postJSON('/chat/v2/ask', body);

            if (status === 200 && data.success) {
                setThreadId(data.threadId);
                setMessages((prev) => [
                    ...prev,
                    {
                        role: 'bot',
                        text: data.answer,
                        docs: data.docs || [],
                        promptId: data.promptId,
                    },
                ]);
            } else {
                setMessages((prev) => [
                    ...prev,
                    { role: 'bot', text: 'Ocurrió un error al procesar tu mensaje.', docs: [] },
                ]);
            }
        } catch {
            setMessages((prev) => [
                ...prev,
                { role: 'bot', text: 'Error de conexión. Intentá de nuevo.', docs: [] },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const onKey = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
    };

    const openReview = (promptId) => {
        setReviewingId(promptId);
        setSelectedPoints(null);
        setReviewComment('');
    };

    const cancelReview = () => {
        setReviewingId(null);
        setSelectedPoints(null);
        setReviewComment('');
    };

    const submitReview = async (promptId) => {
        setSendingReview(true);
        try {
            const { status, data } = await postJSON('/prompts/review', {
                promptId,
                comment: reviewComment,
                points: selectedPoints,
            });
            if (status === 200 || status === 201) {
                setReviewedMap((prev) => ({ ...prev, [promptId]: selectedPoints }));
                cancelReview();
            } else {
                alert(data.message || 'Error al enviar la reseña');
            }
        } catch {
            alert('Error de conexión al enviar la reseña');
        } finally {
            setSendingReview(false);
        }
    };

    const pointsEmoji = (p) => ({ 0: '😭', 1: '🙂', 2: '🤩' }[p] ?? '❓');

    const wrapperStyle = embedded
        ? { ...s.wrapper, position: 'absolute', inset: 0 }
        : s.wrapper;

    return (
        <div style={wrapperStyle}>
            {/* Header */}
            <div style={s.header}>
                <img src="/img/UNITia Color Hor.svg" alt="logo" style={s.logo} />
            </div>

            {/* Messages */}
            <div style={s.messages}>
                {messages.length === 0 && (
                    <p style={s.empty}>¡Hola! Haceme una pregunta</p>
                )}

                {messages.map((msg, i) => (
                    <div
                        key={i}
                        style={{
                            ...s.msgRow,
                            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                        }}
                    >
                        {msg.role === 'user' ? (
                            <p style={{ ...s.msgText, color: '#000', textAlign: 'right' }}>
                                {msg.text}
                            </p>
                        ) : (
                            <div style={s.botBlock}>
                                <p style={{ ...s.msgText, color: ACCENT }}>{msg.text}</p>

                                {/* Documentos */}
                                {msg.docs && msg.docs.length > 0 && (
                                    <div style={s.docsSection}>
                                        <span style={s.docsLabel}>
                                            <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: 6 }} />
                                            Fuentes encontradas ({msg.docs.length})
                                        </span>
                                        <div style={s.docsList}>
                                            {msg.docs.map((doc, idx) => (
                                                <div key={idx} style={s.docCard}>
                                                    <FontAwesomeIcon
                                                        icon={faFileAlt}
                                                        style={{ color: ACCENT, fontSize: '1.1rem', flexShrink: 0 }}
                                                    />
                                                    <div style={s.docInfo}>
                                                        <span style={s.docName}>
                                                            {doc.name}.{doc.extension}
                                                        </span>
                                                        {doc.abstract && (
                                                            <span style={s.docAbstract}>{doc.abstract}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Review */}
                                {msg.promptId && (
                                    <div style={s.reviewSection}>
                                        {reviewedMap[msg.promptId] !== undefined ? (
                                            <span style={s.reviewedText}>
                                                Calificaste esta respuesta:{' '}
                                                <span style={{ fontSize: '1.3rem' }}>
                                                    {pointsEmoji(reviewedMap[msg.promptId])}
                                                </span>
                                            </span>
                                        ) : reviewingId === msg.promptId ? (
                                            <div style={s.reviewForm}>
                                                <span style={s.reviewQuestion}>
                                                    ¿Cómo calificarías esta respuesta?
                                                </span>
                                                <div style={s.emojiRow}>
                                                    {[0, 1, 2].map((p) => (
                                                        <button
                                                            key={p}
                                                            onClick={() => setSelectedPoints(p)}
                                                            disabled={sendingReview}
                                                            style={{
                                                                ...s.emojiBtn,
                                                                background: selectedPoints === p
                                                                    ? ACCENT
                                                                    : 'transparent',
                                                                border: `1px solid ${selectedPoints === p ? ACCENT : '#ddd'}`,
                                                            }}
                                                            title={['Malo', 'Regular', 'Excelente'][p]}
                                                        >
                                                            {pointsEmoji(p)}
                                                        </button>
                                                    ))}
                                                </div>
                                                <textarea
                                                    rows={2}
                                                    placeholder="Comentario opcional..."
                                                    value={reviewComment}
                                                    onChange={(e) => setReviewComment(e.target.value)}
                                                    disabled={sendingReview}
                                                    style={s.reviewTextarea}
                                                />
                                                <div style={s.reviewActions}>
                                                    <button
                                                        onClick={() => submitReview(msg.promptId)}
                                                        disabled={sendingReview || selectedPoints === null}
                                                        style={{
                                                            ...s.reviewConfirmBtn,
                                                            opacity: sendingReview || selectedPoints === null ? 0.5 : 1,
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: 6 }} />
                                                        Confirmar
                                                    </button>
                                                    <button
                                                        onClick={cancelReview}
                                                        disabled={sendingReview}
                                                        style={s.reviewCancelBtn}
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => openReview(msg.promptId)}
                                                style={s.rateBtn}
                                            >
                                                <FontAwesomeIcon icon={faStar} style={{ marginRight: 6 }} />
                                                Calificar respuesta
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}

                {loading && (
                    <div style={{ ...s.msgRow, justifyContent: 'flex-start' }}>
                        <img
                            src="/img/AI Boton.svg"
                            alt="Cargando..."
                            style={s.loadingImg}
                        />
                    </div>
                )}

                <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div style={s.inputBar}>
                <input
                    type="text"
                    placeholder="Escribí tu mensaje..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKey}
                    disabled={loading}
                    style={s.input}
                />
                <button
                    onClick={send}
                    disabled={loading || !input.trim()}
                    style={{
                        ...s.sendBtn,
                        opacity: loading || !input.trim() ? 0.5 : 1,
                    }}
                >
                    <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: '1.4rem' }} />
                </button>
            </div>

            <style>{keyframes}</style>
        </div>
    );
}

const keyframes = `
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
`;

const s = {
    wrapper: {
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    header: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px 20px',
        borderBottom: '1px solid #eee',
    },
    logo: { height: 50, objectFit: 'contain' },
    messages: { flex: 1, overflowY: 'auto', padding: '24px 20px' },
    empty: { textAlign: 'center', color: '#aaa', marginTop: 80, fontSize: '1.1rem' },
    msgRow: { display: 'flex', marginBottom: 20 },
    botBlock: { display: 'flex', flexDirection: 'column', maxWidth: '80%', gap: 12 },
    msgText: { margin: 0, fontSize: '1rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' },

    // Docs
    docsSection: { display: 'flex', flexDirection: 'column', gap: 8 },
    docsLabel: { fontSize: '0.8rem', fontWeight: 600, color: '#999', display: 'flex', alignItems: 'center' },
    docsList: { display: 'flex', flexDirection: 'column', gap: 6 },
    docCard: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 14px',
        border: '1px solid #e8ecff',
        borderRadius: 10,
        backgroundColor: '#f5f7ff',
    },
    docInfo: { display: 'flex', flexDirection: 'column', gap: 2 },
    docName: { fontSize: '0.9rem', fontWeight: 600, color: '#333' },
    docAbstract: { fontSize: '0.8rem', color: '#888' },

    // Review
    reviewSection: { borderTop: '1px solid #f0f0f0', paddingTop: 10 },
    reviewedText: { fontSize: '0.875rem', color: '#999' },
    reviewForm: { display: 'flex', flexDirection: 'column', gap: 8 },
    reviewQuestion: { fontSize: '0.85rem', fontWeight: 600, color: '#666' },
    emojiRow: { display: 'flex', gap: 8 },
    emojiBtn: {
        fontSize: '1.4rem',
        padding: '4px 12px',
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'all 0.15s',
    },
    reviewTextarea: {
        resize: 'none',
        border: '1px solid #ddd',
        borderRadius: 8,
        padding: '8px 12px',
        fontSize: '0.875rem',
        outline: 'none',
        fontFamily: 'inherit',
        width: '100%',
        boxSizing: 'border-box',
    },
    reviewActions: { display: 'flex', gap: 8 },
    reviewConfirmBtn: {
        padding: '6px 14px',
        borderRadius: 8,
        border: 'none',
        backgroundColor: ACCENT,
        color: '#fff',
        fontSize: '0.875rem',
        cursor: 'pointer',
    },
    reviewCancelBtn: {
        padding: '6px 14px',
        borderRadius: 8,
        border: '1px solid #ddd',
        backgroundColor: 'transparent',
        fontSize: '0.875rem',
        cursor: 'pointer',
        color: '#666',
    },
    rateBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '5px 12px',
        borderRadius: 8,
        border: '1px solid #ddd',
        backgroundColor: 'transparent',
        fontSize: '0.8rem',
        color: '#888',
        cursor: 'pointer',
    },

    // Loading
    loadingImg: { height: 40, animation: 'pulse 1.5s ease-in-out infinite' },

    // Input
    inputBar: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 20px',
        borderTop: '1px solid #eee',
        backgroundColor: '#fff',
    },
    input: {
        flex: 1,
        height: 52,
        border: '1px solid #ddd',
        borderRadius: 8,
        padding: '0 14px',
        fontSize: '1rem',
        outline: 'none',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    },
    sendBtn: {
        width: 52,
        height: 52,
        borderRadius: 8,
        border: 'none',
        backgroundColor: ACCENT,
        color: '#fff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
};

export default IOSChat;
